// The PAINT BUCKET tool: one click recolors the whole face-connected region of
// same-coloured voxels under the cursor to the active palette index (a 3D flood
// fill — see interaction/handlers/bucket-fill.ts). It's the Paint tool's colour
// with a flood-fill reach instead of a swept brush.
//
// The fill is a single-shot action with no drag: claims the trigger press, does
// the whole fill in start() inside main's already-open grid transaction, and
// sets no Interaction — so the gesture finalizes through main's generic
// idle/endTransaction tail (an empty fill records nothing; see history.ts).

import { uiState } from "../ui/state";
import { type PickTarget, type CellBounds } from "../interaction/pick";
import { bucketFill } from "../interaction/handlers/bucket-fill";
import { type VoxelGrid } from "../model/voxel-grid";
import { type ToolRegistry } from "../app/tool-registry";

export interface BucketToolDeps {
  tools: ToolRegistry;
  pickFor: (i: number) => PickTarget | null; // the controller's last-frame pick
  // Same gesture-target resolution as Sketch/Paint: a voxel hit → that object's
  // grid. Bucket only fills existing voxels, so the room surface never seeds one.
  resolveGestureTarget: (i: number) => { grid: VoxelGrid };
  cellBounds: CellBounds; // live work-volume bounds (mutated in place by resize)
  // Shift held → recolor EVERY voxel of the seed colour (ignore connectivity).
  globalFill: () => boolean;
}

export function installBucketTool(deps: BucketToolDeps): void {
  deps.tools.register({
    id: "bucket",
    claims: () => uiState.topMode === "bucket",
    start: (i) => {
      const pick = deps.pickFor(i);
      if (!pick) return;
      const { grid } = deps.resolveGestureTarget(i);
      bucketFill(
        pick,
        grid,
        deps.cellBounds,
        uiState.currentPaletteIndex,
        deps.globalFill(),
      );
    },
  });
}
